"use client"

import type { ReactNode } from "react"
import { Suspense, useEffect, useState } from "react"
import { Toaster } from "sonner"
import { AuthProvider } from "@/components/auth-provider"
import { PullToRefresh } from "@/components/pull-to-refresh"
import {
  readAppliedColorTheme,
  type ResolvedColorTheme,
} from "@/lib/color-theme"
import {
  DEFAULT_DISPLAY_ZOOM,
  applyDisplayZoom,
  emitDisplayZoomChange,
  readAppliedDisplayZoom,
  readMirroredDisplayZoom,
  stepDisplayZoom,
} from "@/lib/display-zoom"

/**
 * 表示倍率 (Cmd +/- /0) のショートカット。倍率の計算と DOM 反映は
 * lib/display-zoom.ts に集約。
 *
 * ここは全ページ共通の Providers なので、設定ストア (supabase-client を引き込む)
 * には触らずイベントを投げるだけにする。AppSettings への保存は
 * components/display-zoom-sync.tsx が受け持つ。
 */
function useZoomShortcuts() {
  // ペイント前復元は app/layout.tsx のインラインスクリプトが行う。
  // ここは念のためのフォールバック (スクリプトが動かない環境でも倍率を戻す)
  useEffect(() => {
    const mirrored = readMirroredDisplayZoom()
    if (mirrored !== null && mirrored !== DEFAULT_DISPLAY_ZOOM) {
      applyDisplayZoom(mirrored)
    }
  }, [])

  useEffect(() => {
    const isMac =
      typeof navigator !== "undefined" &&
      /Mac|iPhone|iPad|iPod/.test(navigator.platform)

    const setZoom = (next: number) => {
      emitDisplayZoomChange(applyDisplayZoom(next))
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Mac で Ctrl + ±/0 が押された場合はズーム処理を行わない（Cmd は従来通り）
      if (isMac && e.ctrlKey && !e.metaKey) {
        return
      }
      if (!e.metaKey && !e.ctrlKey) return

      // Cmd/Ctrl + Plus (=) で拡大
      if (e.key === "=" || e.key === "+") {
        e.preventDefault()
        setZoom(stepDisplayZoom(readAppliedDisplayZoom(), 1))
        return
      }
      // Cmd/Ctrl + Minus で縮小
      if (e.key === "-") {
        e.preventDefault()
        setZoom(stepDisplayZoom(readAppliedDisplayZoom(), -1))
        return
      }
      // Cmd/Ctrl + 0 でリセット
      if (e.key === "0") {
        e.preventDefault()
        setZoom(DEFAULT_DISPLAY_ZOOM)
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [])
}

/**
 * いま `<html>` に当たっているテーマ。sonner の Toaster は自前の配色を持つため、
 * ここだけは「解決後のテーマ」を値として知る必要がある。
 *
 * クラスを付けるのは lib/color-theme.ts の applyColorTheme だけなので、
 * ここは設定ストア (supabase-client を引き込む) を import せずに
 * DOM を観測するだけにしている。Providers は LP や規約ページも通るため、
 * それらの共通チャンクにアプリ本体の依存を載せたくない。
 */
function useResolvedColorTheme(): ResolvedColorTheme {
  // SSR/静的 HTML は dark 前提 (app/layout.tsx の <html className="dark">)。
  // 初期値を揃えておかないとハイドレーション不一致になる
  const [resolved, setResolved] = useState<ResolvedColorTheme>("dark")

  useEffect(() => {
    const read = () => setResolved(readAppliedColorTheme())
    read()
    const observer = new MutationObserver(read)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    })
    return () => observer.disconnect()
  }, [])

  return resolved
}

export function Providers({ children }: { children: ReactNode }) {
  useZoomShortcuts()
  const colorTheme = useResolvedColorTheme()

  return (
    <AuthProvider>
      <PullToRefresh />
      <Suspense fallback={null}>{children}</Suspense>
      <Toaster
        position="top-center"
        theme={colorTheme}
        toastOptions={{
          style: {
            background: "var(--color-surface)",
            color: "var(--color-text)",
            border: "1px solid var(--color-divider)",
            borderRadius: "12px",
            boxShadow: "var(--shadow-overlay)",
          },
        }}
      />
    </AuthProvider>
  )
}
